import { intradayAlignmentRoute } from './alignmentRoute'
import { logger } from "@/utils/logger";
import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/user";
import MainLayout from "@/layouts/MainLayout.vue";
import { recoverFromChunkLoadError } from "@/utils/chunkLoadRecovery";

export const DUALLEG_ACCOUNT_ANALYSIS_PATH = "/home/dualleg/account-analysis";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // 默认重定向到Landing页面
    { path: "/", redirect: "/home/landing" },

    // 登录页面
    {
      path: "/login",
      name: "登录",
      component: () => import("@/views/Login.vue"),
      meta: {
        title: "登录",
        requireAuth: false,
        layout: "blank",
      },
    },

    // 测试登录页面（开发用）
    {
      path: "/test-login",
      name: "测试登录",
      component: () => import("@/views/TestLogin.vue"),
      meta: {
        title: "测试登录",
        requireAuth: false,
        layout: "blank",
      },
    },

    // 基础信息测试页面（开发用）
    {
      path: "/basic-info-test",
      name: "基础信息测试",
      component: () => import("@/views/BasicInfoTest.vue"),
      meta: {
        title: "基础信息测试",
        requireAuth: false,
        layout: "blank",
      },
    },

    // LOGIC动作统计设计方案Demo（开发用）
    {
      path: "/demo/logic-action-design",
      name: "LOGIC动作统计设计Demo",
      component: () => import("@/views/demo/LogicActionDesignDemo.vue"),
      meta: {
        title: "LOGIC动作统计设计方案对比",
        requireAuth: false,
        layout: "blank",
      },
    },

    // 主应用路由
    {
      path: "/home",
      // 移除父路由 name，避免与空路径子路由冲突的警告
      component: MainLayout,
      meta: { requireAuth: true },
      children: [
        // 默认子路由需命名，以消除 Vue Router 警告
        { path: "", name: "主页面", redirect: "landing" },

        // Landing页面 - 首页
        {
          path: "landing",
          name: "LandingPage",
          component: () => import("@/views/LandingPage.vue"),
          meta: {
            title: "首页",
            requireAuth: true,
          },
        },
        // 风险监控
        {
          path: "fundRiskMonitorV2",
          name: "风险监控",
          component: () => import("@/views/risk/Monitor.vue"),
          meta: {
            title: "风险监控",
            requireAuth: true,
            permissions: ["fundRiskMonitorV2_read"],
          },
        },
        {
          path: "tradingDesk",
          name: "交易助手",
          component: () => import("@/views/trading/TradingDesk.vue"),
          meta: { title: "交易助手", requireAuth: true, permissions: ["exposureAdjustment_read"], exactPermissions: true },
        },
        {
          // V3: 下方两张表改 Tab 切换 + 敞口表多一层「持仓来自哪个账户」下钻。
          // 独立权限位 fundRiskMonitorV3_read, 灰度期间只授给验收的人, 与 V2 互不影响。
          path: "fundRiskMonitorV3",
          name: "风险监控V3",
          component: () => import("@/views/risk/MonitorV3.vue"),
          meta: {
            title: "风险监控V3",
            requireAuth: true,
            permissions: ["fundRiskMonitorV3_read"],
          },
        },
        {
          path: "fundRiskMonitorV5",
          name: "风险监控V5",
          component: () => import("@/views/risk/MonitorV5.vue"),
          meta: {
            title: "风险监控V5",
            requireAuth: true,
            permissions: ["fundRiskMonitorV3_read"],
          },
        },
        {
          path: "nightRiskMonitor",
          name: "夜盘监控",
          component: () => import("@/views/risk/NightMonitor.vue"),
          meta: {
            title: "夜盘监控",
            requireAuth: true,
            permissions: ["nightRiskMonitor_read"],
          },
        },
        {
          path: "pendingOrderTradeTable",
          name: "成交信息",
          component: () => import("@/views/risk/TradeInfo.vue"),
          meta: {
            title: "成交信息",
            requireAuth: true,
            permissions: ["pendingOrderTradeTable_read"],
          },
        },
        {
          path: "orderBlotter",
          name: "委托明细",
          component: () => import("@/views/risk/OrderBlotter.vue"),
          meta: {
            title: "委托明细",
            requireAuth: true,
            permissions: ["orderBlotter_read"],
          },
        },
        {
          path: "futureCalManualOrder",
          name: "跨期手工报单",
          component: () => import("@/views/calendar/ManualOrderDesk.vue"),
          meta: {
            title: "跨期手工报单",
            requireAuth: true,
            permissions: ["futureCalStrategy_read"],
          },
        },

        {
          path: 'opportunityDashboard',
          name: '机会汇总看板',
          component: () => import('@/views/OpportunityDashboard/OpportunityDashboardPage.vue'),
          meta: {
            title: '机会汇总看板',
            requireAuth: true,
            permissions: ['opportunityDashboard_read'],
          },
        },
        {
          path: 'scenario-cases',
          name: 'ScenarioCaseList',
          component: () => import('@/views/scenario-cases/Index.vue'),
          meta: {
            title: '动作场景案例',
            requireAuth: true,
            permissions: ['scenarioCases_read'],
          },
        },
        {
          path: 'scenario-cases/:trade_date/:variety',
          name: 'ScenarioCaseDetail',
          component: () => import('@/views/scenario-cases/Detail.vue'),
          meta: {
            title: '案例详情',
            requireAuth: true,
            permissions: ['scenarioCases_read'],
          },
        },
        {
          path: 'scenario-cases/:trade_date/:variety/:contract',
          name: 'ScenarioCaseDetailContract',
          component: () => import('@/views/scenario-cases/Detail.vue'),
          meta: {
            title: '案例详情',
            requireAuth: true,
            permissions: ['scenarioCases_read'],
          },
        },

        // 基差管理系统（独立模块）
        {
          path: "main-variety-basis-threshold",
          name: "主品种基差阈值",
          component: () => import("@/views/basis-management/MainVarietyBasisThreshold.vue"),
          meta: {
            title: "主品种基差阈值管理",
            requireAuth: true,
            permissions: ["main_variety_basis_threshold_read_update"],
          },
        },
        {
          path: "exposure-map-management",
          name: "对冲映射管理",
          component: () =>
            import("@/views/basis-management/exposure-map/ExposureMapManagement.vue"),
          meta: {
            title: "对冲映射管理",
            requireAuth: true,
            permissions: ["exposure_map_read", "exposure_map_update"],
          },
        },
        {
          path: "priceArbitrage",
          name: "折溢价套利",
          component: () => import("@/views/trading/PriceArbitrage.vue"),
          meta: {
            title: "折溢价套利",
            requireAuth: true,
            permissions: ["priceArbitrage_read"],
          },
        },
        {
          path: "priceArbitrage/param-adjustments",
          name: "折溢价操作台",
          component: () => import("@/views/trading/PriceArbitrageParamAdjustments.vue"),
          meta: {
            title: "折溢价操作台",
            requireAuth: true,
            permissions: ["priceArbitrageConsole_read"],
          },
        },
        {
          path: "volatilityTrading",
          name: "波动率交易",
          component: () => import("@/views/trading/VolatilityTrading.vue"),
          meta: {
            title: "波动率交易",
            requireAuth: true,
            permissions: ["volatilityTrading_read"],
          },
        },
        {
          path: "convertBondArb",
          name: "转债套利",
          component: () => import("@/views/trading/convertible-bond/ConvertBondArb.vue"),
          meta: {
            title: "转债套利",
            requireAuth: true,
            permissions: ["convertBondArb_read"],
          },
        },
        {
          path: "duallegTrader",
          name: "双腿价差",
          component: () => import("@/views/trading/DualLeg.vue"),
          meta: {
            title: "双腿价差",
            requireAuth: true,
            permissions: ["duallegTrader_read"],
          },
        },
        {
          path: "dualleg/account-analysis",
          name: "DualLegAccountAnalysis",
          component: () => import("@/views/trading/dualleg/DualLegAccountAnalysis.vue"),
          meta: {
            title: "DualLeg 账户评估",
            requireAuth: true,
            permissions: ["dualleg_account_analysis_read"],
          },
        },
        {
          path: "dualleg/account-analysis/:accountId",
          name: "DualLegAccountLogics",
          component: () => import("@/views/trading/dualleg/DualLegAccountAnalysis.vue"),
          meta: {
            title: "DualLeg 账户 Logic 明细",
            requireAuth: true,
            permissions: ["dualleg_account_analysis_read"],
          },
        },
        {
          path: "dualleg/account-analysis/:accountId/:logicId",
          name: "DualLegLogicTrades",
          component: () => import("@/views/trading/dualleg/DualLegAccountAnalysis.vue"),
          meta: {
            title: "DualLeg Logic 交易记录",
            requireAuth: true,
            permissions: ["dualleg_account_analysis_read"],
          },
        },
        {
          path: "duallegBaseline",
          name: "双腿基准设置",
          component: () => import("@/views/trading/DualLegBaseline.vue"),
          meta: {
            title: "双腿基准设置",
            requireAuth: true,
            permissions: ["duallegBaseline_read_update"],
          },
        },
        {
          path: "hedgedConvert",
          name: "对冲申赎",
          component: () => import("@/views/trading/HedgedConvert.vue"),
          meta: {
            title: "对冲申赎",
            requireAuth: true,
            permissions: ["hedgedConvert_read_update"],
          },
        },
        {
          path: "pcpMulti",
          name: "PCP多账户",
          component: () => import("@/views/trading/pcp-multi/PcpMulti.vue"),
          meta: {
            title: "PCP多账户",
            requireAuth: true,
            permissions: ["pcpMulti_read_update"],
          },
        },
        {
          path: "pcpMulti/opening-workbench",
          name: "PcpOpeningWorkbench",
          component: () => import("@/views/trading/pcp-multi/PcpOpeningWorkbench.vue"),
          meta: {
            title: "开盘工作台",
            requireAuth: true,
            permissions: ["pcpMulti_read_update"],
          },
        },
        {
          path: "pcpMulti/signal-calculation",
          name: "历史分析",
          component: () => import("@/views/trading/pcp-multi/SignalCalculation.vue"),
          meta: {
            title: "历史分析",
            requireAuth: true,
            permissions: ["pcpMulti_signal_calculation_read"],
          },
        },
        {
          path: "pcpMulti/realtime-monitor",
          name: "实时信号",
          component: () => import("@/views/trading/pcp-multi/RealtimeMonitor.vue"),
          meta: {
            title: "实时信号",
            requireAuth: true,
            permissions: ["pcpMulti_realtime_monitor_read"],
          },
        },
        {
          path: "pcpMulti/fill-pnl",
          name: "PcpMultiFillPnl",
          component: () => import("@/views/trading/pcp-multi/PcpMultiFillPnl.vue"),
          meta: {
            title: "各基金品种收益",
            requireAuth: true,
            permissions: ["pcpMulti_fill_pnl_read"],
          },
        },
        {
          path: "pcpMulti/account-analysis/:pathMatch(.*)*",
          redirect: "/home/pcpMulti",
        },
        // 注：PCP信号回放（pcpMulti/signal-replay）路由已下线（2026-06-08，功能废弃）
        {
          path: 'pcpMulti/basis-workbench',
          name: 'BasisWorkbench',
          component: () => import('@/views/trading/pcp-multi/BasisWorkbench.vue'),
          meta: {
            title: '基差工作台',
            requireAuth: true,
            permissions: ['pcpMulti_basis_workbench_read_update'],
          }
        },
        intradayAlignmentRoute,
        {
          path: 'signal-spread',
          name: 'SignalSpreadBoard',
          component: () => import('@/views/trading/SignalSpreadBoard.vue'),
          meta: {
            title: '信号价差',
            requireAuth: true,
            permissions: ['signal_spread_board_read'],
          }
        },
        {
          path: 'pcpOpeningExecution',
          name: 'PcpOpeningExecution',
          component: () => import('@/views/trading/pcp-multi/PcpOpeningExecution.vue'),
          meta: {
            title: 'ETF 自动交易台',
            requireAuth: true,
            permissions: ['pcpOpeningExecution_read_update'],
          }
        },
        {
          path: 'pcpMulti/opening-execution-shadow',
          name: 'PcpOpeningExecutionShadow',
          redirect: '/home/pcpMulti/opening-execution-preprod',
          meta: {
            title: 'ETF 自动交易台（预发）',
            requireAuth: true,
            permissions: ['pcpOpeningExecution_read_update'],
          },
        },
        {
          path: 'pcpMulti/opening-execution-preprod',
          name: 'PcpOpeningExecutionPreprod',
          component: () => import('@/views/trading/pcp-multi/PcpOpeningExecution.vue'),
          props: { scope: 'preprod' },
          meta: {
            title: 'ETF 自动交易台（预发）',
            requireAuth: true,
            permissions: ['pcpOpeningExecution_read_update'],
          },
        },
        {
          path: 'pcpOpeningReplay',
          redirect: '/home/pcpOpeningExecution',
        },
        {
          path: 'pcpMulti/future-pcp-plan',
          name: 'FuturePcpPlan',
          component: () => import('@/views/trading/pcp-multi/FuturePcpPlan.vue'),
          meta: {
            title: '期货PCP计划',
            requireAuth: true,
            permissions: ['future_pcp_plan_read_update'],
          }
        },
        {
          path: 'pcpMulti/derivative-basis-comparison',
          name: 'DerivativeBasisComparison',
          component: () => import('@/views/trading/pcp-multi/DerivativeBasisComparison.vue'),
          meta: {
            title: '指数衍生品年化基差对比',
            requireAuth: true,
            permissions: ['index_derivatives_basis_read'],
          }
        },
        {
          path: 'pcpMulti/basis-monitor-table',
          name: 'BasisMonitorTable',
          component: () => import('@/views/trading/pcp-multi/BasisMonitorTable.vue'),
          meta: {
            title: '基差实时监控表',
            requireAuth: true,
            permissions: ['index_derivatives_basis_read'],
          },
        },
        // {
        //   path: "pcpmulti/param-history/batch/:batchId",
        //   name: "PCP参数历史批次汇总",
        //   component: () => import("@/views/trading/pcp-multi/ParamHistoryBatchSummary.vue"),
        //   meta: {
        //     title: "批次汇总",
        //     requireAuth: true,
        //     permissions: ["pcpMulti_manager_read_update"],
        //   },
        // },
        {
          path: "optionGroup",
          name: "期权组合",
          component: () => import("@/views/trading/option-group/OptionGroupPage.vue"),
          meta: {
            title: "换月工作台",
            requireAuth: true,
            permissions: ["optionGroup_read_update"],
          },
        },
        {
          path: "intradayReplay",
          name: "IntradayReplay",
          component: () => import("@/views/trading/IntradayReplay.vue"),
          meta: {
            title: "日内回放",
            requireAuth: true,
            permissions: ["intraday_execution_read"],
            roles: ["admin"],
          },
        },
        {
          path: "intradayExecution",
          name: "IntradayExecution",
          component: () => import("@/views/trading/IntradayExecution.vue"),
          props: { layer: "live" },
          meta: {
            title: "日内策略执行器 · 实盘",
            requireAuth: true,
            permissions: ["intraday_execution_read"],
            roles: ["admin"],
          },
        },
        {
          path: "intradayWjStaging",
          name: "IntradayWjStaging",
          component: () => import("@/views/trading/IntradayExecution.vue"),
          props: { layer: "staging" },
          meta: {
            title: "日内策略执行器 · 预发 · 东北信用",
            requireAuth: true,
            permissions: ["intraday_execution_read"],
            roles: ["admin"],
          },
        },
        {
          path: "etfT0",
          name: "ETF-T0",
          component: () => import("@/views/trading/EtfT0.vue"),
          meta: {
            title: "ETF-T0",
            requireAuth: true,
            permissions: ["etfT0_read"],
          },
        },
        // {
        //   path: "optPcpArbitrage",
        //   name: "期权平价",
        //   component: () => import("@/views/trading/option-parity/OptionParity.vue"),
        //   meta: {
        //     title: "期权平价套利",
        //     requireAuth: true,
        //     permissions: ["optPcpArbitrage_read_update"],
        //   },
        // },
        {
          path: "futuresSpread",
          name: "期货价差",
          component: () => import("@/views/trading/FuturesSpread.vue"),
          meta: {
            title: "期货价差",
            requireAuth: true,
            permissions: ["futuresSpread_read"],
          },
        },
        {
          path: "futureEtfControlPanel",
          name: "操作台",
          component: () => import("@/views/trading/ControlPanel.vue"),
          meta: {
            title: "操作台",
            requireAuth: true,
            permissions: ["futureEtfControlPanel_read"],
          },
        },

        // 清算信息
        {
          path: "FundEquity",
          name: "交易帐",
          component: () => import("@/views/clearing/FundEquity.vue"),
          meta: {
            title: "交易帐",
            requireAuth: true,
            permissions: ["FundEquity_read"],
          },
        },
        {
          path: "GoldArbRecon",
          name: "黄金套利对账",
          component: () => import("@/views/clearing/GoldArbRecon.vue"),
          meta: {
            title: "黄金套利对账",
            requireAuth: true,
            permissions: ["gold_arb_read"],
          },
        },
        {
          path: "otcMarginMng",
          name: "场外头寸管理",
          component: () => import("@/views/clearing/OtcMargin.vue"),
          meta: {
            title: "场外头寸管理",
            requireAuth: true,
            permissions: ["otcMarginMng_read"],
          },
        },
        {
          path: "EtfConvertMonitor",
          name: "ETF申赎监控",
          component: () => import("@/views/clearing/EtfConvertMonitor.vue"),
          meta: {
            title: "ETF申赎监控",
            requireAuth: true,
            permissions: ["EtfConvertMonitor_read"],
          },
        },
        {
          path: "etfCreationSchedules",
          name: "跨境ETF定时申购",
          component: () => import("@/views/clearing/EtfCreationSchedules.vue"),
          meta: {
            title: "跨境ETF定时申购",
            requireAuth: true,
            permissions: ["EtfConvertMonitor_read"],
          },
        },
        {
          path: "etfConvertBacktest",
          name: "ETF折溢价回测",
          component: () => import("@/views/clearing/EtfBacktest.vue"),
          meta: {
            title: "ETF折溢价回测",
            requireAuth: true,
            permissions: ["etfConvertBacktest_read"],
          },
        },
        {
          path: "cashTargetMng",
          name: "现金管理",
          component: () => import("@/views/clearing/CashTargetManagement.vue"),
          meta: {
            title: "现金管理",
            requireAuth: true,
            permissions: ["cashTargetMng_read"],
          },
        },

        // AI交易驾驶舱 - 所有用户可访问，不做权限控制
        {
          path: "copilot",
          name: "AI交易驾驶舱",
          component: () => import("@/views/Copilot.vue"),
          meta: {
            title: "AI交易驾驶舱",
            requireAuth: true,
            // 不设置permissions，所有登录用户都可访问
          },
        },

        {
          path: "reports/arbitrage-daily",
          name: "套利交易日报",
          component: () => import("@/views/reports/ArbitrageDailyReport.vue"),
          meta: {
            title: "套利交易日报",
            requireAuth: true,
            permissions: ["report_daily_view"],
          },
        },

        // 日报中心
        {
          path: "daily-report/etf-validator",
          name: "ETF变动日报",
          component: () => import("@/views/daily-report/EtfValidatorReport.vue"),
          meta: {
            title: "ETF变动日报",
            requireAuth: true,
            permissions: ["daily_report_etf_validator_read"],
          },
        },

        // 运维中心 - ETF现金偏差与涨跌停盈亏监控
        {
          path: "etf-cashdif-limit-pnl",
          name: "ETF现金偏差与涨跌停盈亏",
          component: () => import("@/views/ops/EtfCashdifLimitPnl.vue"),
          meta: {
            title: "ETF现金偏差与涨跌停盈亏监控",
            requireAuth: true,
            permissions: ["etf_cashdif_limit_pnl_read"],
          },
        },
        {
          path: "ipo/bj-pnl",
          name: "BjIpoPnlMonitor",
          component: () => import("@/views/ipo/BjIpoPnlMonitor.vue"),
          meta: {
            title: "新股收益监控",
            requireAuth: true,
            permissions: ["bj_ipo_pnl_read"],
          },
        },

        // 权限调试页面（开发用）
        {
          path: "debug-permissions",
          name: "权限调试",
          component: () => import("@/views/DebugPermissions.vue"),
          meta: {
            title: "权限调试工具",
            requireAuth: true,
          },
        },

        // 券商ETF资质管理
        {
          path: "broker-etf-qualification",
          name: "券商ETF资质",
          component: () => import("@/views/system/BrokerEtfQualification.vue"),
          meta: {
            title: "券商ETF资质管理",
            requireAuth: true,
            permissions: ["broker_etf_qualification_read"],
          },
        },

        // 基差交易训练
        // {
        //   path: "training/basis",
        //   name: "基差交易训练",
        //   component: () => import("@/views/training/basis/BasisTraining.vue"),
        //   meta: {
        //     title: "基差交易训练",
        //     requireAuth: true,
        //     permissions: ["basis_training_read_update"],
        //   },
        // },

        // 访问提示页面
        {
          path: "empty",
          name: "访问提示",
          component: () => import("@/views/NotFound.vue"),
          meta: {
            title: "访问提示",
            requireAuth: true,
          },
        },
      ],
    },

    // 404页面
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () => import("@/views/NotFound.vue"),
      meta: {
        title: "页面未找到",
      },
    },
  ],
});

// 导航菜单配置（完全匹配老项目）
export const menus = [
  {
    icon: "Warning",
    name: "风险监控",
    children: [
      { path: "/home/fundRiskMonitorV2", icon: "TrendCharts", name: "风险监控" },
      { path: "/home/fundRiskMonitorV3", icon: "TrendCharts", name: "风险监控V3" },
      { path: "/home/fundRiskMonitorV5", icon: "TrendCharts", name: "风险监控V5" },
      { path: "/home/nightRiskMonitor", icon: "Moon", name: "夜盘监控" },
      { path: "/home/pendingOrderTradeTable", icon: "List", name: "成交信息" },
      { path: "/home/orderBlotter", icon: "Tickets", name: "委托明细" },
    ],
  },
  {
    icon: "Goods",
    name: "折溢价套利",
    children: [
      { path: "/home/priceArbitrage", icon: "TrendCharts", name: "折溢价套利" },
      { path: "/home/priceArbitrage/param-adjustments", icon: "Operation", name: "折溢价操作台" },
    ],
  },
  {
    icon: "DataLine",
    name: "日内预测",
    children: [
      { path: "/home/intradayExecution", icon: "Monitor", name: "日内策略执行器 · 实盘" },
      { path: "/home/intradayWjStaging", icon: "Monitor", name: "日内策略执行器 · 预发 · 东北信用" },
      { path: "/home/intradayReplay", icon: "VideoPlay", name: "日内回放" },
    ],
  },
  {
    icon: "Tools",
    name: "操作台",
    children: [
      { path: "/home/tradingDesk", icon: "Tools", name: "交易助手" },
      // { path: "/home/tradeRecord", icon: "TrendCharts", name: "期现套利" },
      // { path: '/home/EtfStockConvert', icon: 'TrendCharts', name: 'ETF/股票' },
      // { path: '/home/futuresSpread', name: '期货价差', icon: 'Shuffle' },
      // { path: '/home/futureEtfControlPanel', name: 'ETF手工申赎', icon: 'Tools' },
      { path: "/home/volatilityTrading", icon: "TrendCharts", name: "波动率交易" },
      { path: "/home/convertBondArb", icon: "Document", name: "转债套利" },
      { path: "/home/duallegTrader", icon: "TrendCharts", name: "双腿价差" },
      { path: "/home/duallegBaseline", icon: "Setting", name: "双腿基准设置" },
      { path: "/home/optionGroup", icon: "Connection", name: "期权组合" },
      { path: "/home/futureCalManualOrder", icon: "Edit", name: "跨期手工报单" },
      { path: "/home/etfT0", icon: "TrendCharts", name: "ETF-T0" },
      // { path: "/home/optPcpArbitrage", icon: "TrendCharts", name: "期权平价" },
    ],
  },
  {
    icon: "DataAnalysis",
    name: "基差交易",
    children: [
      { path: "/home/pcpMulti", icon: "Grid", name: "PCP多账户" },
      { path: "/home/pcpMulti/opening-workbench", icon: "List", name: "开盘工作台" },
      { path: "/home/hedgedConvert", icon: "Switch", name: "对冲申赎" },
      { path: "/home/opportunityDashboard", icon: "DataAnalysis", name: "机会汇总看板" },
      { path: "/home/scenario-cases", icon: "Histogram", name: "动作场景案例" },
      { path: "/home/pcpMulti/signal-calculation", icon: "DataAnalysis", name: "历史分析" },
      { path: "/home/pcpMulti/realtime-monitor", icon: "Monitor", name: "实时信号" },
      { path: "/home/pcpMulti/fill-pnl", icon: "Money", name: "各基金品种收益" },
      { path: DUALLEG_ACCOUNT_ANALYSIS_PATH, icon: "DataAnalysis", name: "双腿账户评估" },
      // { path: "/home/pcpMulti/manager", icon: "Setting", name: "PCP参数管理器" },
      { path: "/home/pcpMulti/basis-workbench", icon: "Compass", name: "基差工作台" },
      { path: "/home/signal-spread", icon: "TrendCharts", name: "信号价差" },
      { path: "/home/pcpOpeningExecution", icon: "VideoPlay", name: "ETF 自动交易台" },
      {
        path: "/home/pcpMulti/opening-execution-preprod",
        icon: "Operation",
        name: "ETF 自动交易台（预发）",
      },
      { path: "/home/pcpMulti/future-pcp-plan", icon: "DataLine", name: "期货PCP计划" },
      { path: "/home/pcpMulti/derivative-basis-comparison", icon: "DataAnalysis", name: "指数衍生品年化基差对比" },
      { path: "/home/pcpMulti/basis-monitor-table", icon: "DataAnalysis", name: "基差实时监控表" },
      { path: "/home/main-variety-basis-threshold", icon: "SetUp", name: "主品种基差阈值" },
      { path: "/home/exposure-map-management", icon: "DataLine", name: "对冲映射管理" },
      { path: "/home/cashTargetMng", icon: "Wallet", name: "现金管理" },
    ],
  },
  {
    name: "清算信息",
    icon: "Wallet",
    children: [
      { path: "/home/FundEquity", icon: "Wallet", name: "交易帐" },
      { path: "/home/GoldArbRecon", icon: "Document", name: "黄金套利对账" },
      { path: "/home/otcMarginMng", icon: "Document", name: "场外头寸管理" },
      { path: "/home/EtfConvertMonitor", icon: "Document", name: "ETF申赎监控" },
      { path: "/home/etfCreationSchedules", icon: "Calendar", name: "跨境ETF定时申购" },
      { path: "/home/etfConvertBacktest", icon: "Document", name: "ETF折溢价回测" },
    ],
  },
  { path: "/home/copilot", name: "AI交易驾驶舱", icon: "Cpu" },
  {
    icon: "Document",
    name: "报告中心",
    children: [
      { path: "/home/reports/arbitrage-daily", icon: "DataAnalysis", name: "套利交易日报" },
    ],
  },
  {
    icon: "Memo",
    name: "运维中心",
    children: [
      { path: "/home/daily-report/etf-validator", icon: "DataAnalysis", name: "ETF变动日报" },
      { path: "/home/etf-cashdif-limit-pnl", icon: "DataLine", name: "ETF现金偏差与涨跌停" },
    ],
  },
  // {
  //   icon: "VideoPlay",
  //   name: "交易训练",
  //   children: [{ path: "/home/training/basis", icon: "TrendCharts", name: "基差交易训练" }],
  // },
  {
    icon: "Money",
    name: "打新收益",
    children: [{ path: "/home/ipo/bj-pnl", icon: "TrendCharts", name: "新股收益监控" }],
  },
  // 调试工具栏目 - 仅在开发环境显示
  ...(import.meta.env.DEV
    ? [
        {
          icon: "Setting",
          name: "调试工具",
          children: [{ path: "/home/debug-permissions", icon: "Tools", name: "权限调试" }],
        },
      ]
    : []),
  {
    icon: "Setting",
    name: "系统管理",
    children: [
      { path: "/home/broker-etf-qualification", icon: "Document", name: "券商ETF资质" },
    ],
  },
];

// 路由守卫
router.beforeEach(async (to, from, next) => {
  logger.log(`🔍 路由守卫执行: ${from.path} -> ${to.path}`);

  const userStore = useUserStore();
  logger.log(`🔍 用户认证状态: ${userStore.isAuthenticated}`);

  // ✨ 白名单检查：仅在开发环境启用，用于绕过认证直接访问路由
  const devWhitelist = import.meta.env.DEV
    ? ["/home/debug-permissions"]
    : [];

  // ✅ 如果路由在白名单中，直接放行（优先级最高，无需认证）
  const isInWhitelist = devWhitelist.some((path) => to.path.startsWith(path));
  logger.log(`🔍 白名单检查: ${isInWhitelist}, 目标路径: ${to.path}`);

  if (isInWhitelist) {
    logger.log("🔓 开发白名单路由：无需认证，直接访问", to.path);
    // 即使是白名单路由，也尝试恢复用户状态（用于显示菜单和权限过滤）
    const { checkAndRestoreAuth } = await import("@/utils/authCheck");
    await checkAndRestoreAuth();
    next();
    return;
  }

  // 检查是否需要认证（默认需要，除非明确设置为false）
  if (to.meta.requireAuth !== false) {
    // 导入检查认证函数
    const { checkAndRestoreAuth } = await import("@/utils/authCheck");

    // 尝试恢复认证状态
    const isAuthenticated = await checkAndRestoreAuth({
      waitForIdentity: Array.isArray(to.meta.roles) && to.meta.roles.length > 0,
    });

    if (!isAuthenticated) {
      logger.log("用户未认证，跳转到登录页面");
      next("/login");
      return;
    }
  }

  // 已登录用户访问登录页，重定向到Landing页面
  if (to.path === "/login" && userStore.isAuthenticated) {
    next("/home/landing");
    return;
  }

  // 如未加载过权限，尝试按需加载
  if (
    userStore.isAuthenticated &&
    (!userStore.currentUser.permissions || userStore.currentUser.permissions.length === 0)
  ) {
    try {
      await userStore.fetchPermissions();
    } catch {}
  }

  // 检查权限
  if (userStore.isAuthenticated && !userStore.canAccessRoute(to)) {
    try {
      const required = (to.meta?.permissions as string[]) || [];
      const have = userStore.currentUser.permissions;
      logger.warn("权限不足：", { path: to.fullPath, required, have });
    } catch {}
    logger.log("用户权限不足，跳转到Landing页面 /home/landing");
    next("/home/landing");
    return;
  }

  next();
});

// 路由后置守卫 - 设置页面标题
router.afterEach((to) => {
  const title = to.meta?.title as string;
  if (title) {
    document.title = `${title} - 套利宝`;
  } else {
    document.title = "套利宝 - 期现套利交易系统";
  }
});

router.onError((error) => {
  if (recoverFromChunkLoadError(error)) return;

  logger.error("路由错误:", error);
});

export default router;
